import type { Location } from 'utils/types';

const STORAGE_PREFIX = 'tempstars:office-ce-location:v1';
const STORAGE_VERSION = 1;

export type OfficeCeLocation = {
  id: number;
  label: string;
  city: string | null;
  provinceState: string | null;
};

export type OfficeLocationSource = Pick<
  Location,
  'id' | 'practiceName' | 'city' | 'province' | 'removed'
> & {
  deactivated?: number | null;
};

export type SafeOfficeLocationSource = {
  id: number;
  label: string;
  city: string | null;
  provinceState: string | null;
};

export type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

type StoredLocationEnvelope = {
  version: typeof STORAGE_VERSION;
  userId: string;
  locationId: number;
};

export type SavedOfficeLocation = {
  hadSelection: boolean;
  locationId: number | null;
};

export type OfficeLocationSelection =
  | {
      status: 'empty';
      locations: OfficeCeLocation[];
      staleSelectionCleared: boolean;
    }
  | {
      status: 'selection_required';
      locations: OfficeCeLocation[];
      staleSelectionCleared: boolean;
    }
  | {
      status: 'selected';
      locations: OfficeCeLocation[];
      selectedLocation: OfficeCeLocation;
      selectionSource: 'saved' | 'automatic';
      staleSelectionCleared: false;
    };

const cleanText = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const cleaned = value.trim();
  return cleaned || null;
};

const isValidLocationId = (value: unknown): value is number => (
  typeof value === 'number' && Number.isSafeInteger(value) && value > 0
);

const dedupeLocations = (locations: OfficeCeLocation[]): OfficeCeLocation[] => {
  const seen = new Set<number>();
  return locations.filter(location => {
    if (seen.has(location.id)) return false;
    seen.add(location.id);
    return true;
  });
};

/**
 * Project the full office Location response down to the non-sensitive fields
 * the CE picker may render. Removed and deactivated offices are deliberately
 * excluded even though the older getValidLocations helper filters only removed.
 */
export const toOfficeCeLocations = (
  locations: readonly OfficeLocationSource[],
): OfficeCeLocation[] => dedupeLocations(locations
  .filter(location => (
    isValidLocationId(location?.id)
      && Number(location?.removed) !== 1
      && Number(location?.deactivated) !== 1
  ))
  .map(location => ({
    id: location.id,
    label: cleanText(location.practiceName) || `Location ${location.id}`,
    city: cleanText(location.city),
    provinceState: cleanText(location.province),
  })));

/**
 * LOCATION_SELECTION_REQUIRED may return a sanitized list from the shared
 * directory boundary. Keep only the same picker-safe fields and never accept
 * an address, contact field, or provider URL into office-owned state.
 */
export const toOfficeCeLocationsFromSafeList = (
  locations: readonly SafeOfficeLocationSource[],
): OfficeCeLocation[] => dedupeLocations(locations
  .filter(location => isValidLocationId(location?.id))
  .map(location => ({
    id: location.id,
    label: cleanText(location.label) || `Location ${location.id}`,
    city: cleanText(location.city),
    provinceState: cleanText(location.provinceState),
  })));

export const getOfficeCeLocationStorageKey = (userId: number | string): string => (
  `${STORAGE_PREFIX}:${encodeURIComponent(String(userId))}`
);

export const readSavedOfficeCeLocation = (
  storage: StorageLike,
  userId: number | string,
): SavedOfficeLocation => {
  const key = getOfficeCeLocationStorageKey(userId);
  let raw: string | null = null;
  try {
    raw = storage.getItem(key);
  } catch {
    return { hadSelection: false, locationId: null };
  }
  if (raw == null) return { hadSelection: false, locationId: null };

  try {
    const parsed = JSON.parse(raw) as Partial<StoredLocationEnvelope>;
    if (
      parsed.version !== STORAGE_VERSION
      || parsed.userId !== String(userId)
      || !isValidLocationId(parsed.locationId)
    ) {
      storage.removeItem(key);
      return { hadSelection: true, locationId: null };
    }
    return { hadSelection: true, locationId: parsed.locationId };
  } catch {
    try {
      storage.removeItem(key);
    } catch {
      // Storage can be unavailable in hardened browser contexts.
    }
    return { hadSelection: true, locationId: null };
  }
};

export const saveOfficeCeLocation = (
  storage: StorageLike,
  userId: number | string,
  locationId: number,
): void => {
  if (!isValidLocationId(locationId)) return;
  const value: StoredLocationEnvelope = {
    version: STORAGE_VERSION,
    userId: String(userId),
    locationId,
  };
  try {
    storage.setItem(getOfficeCeLocationStorageKey(userId), JSON.stringify(value));
  } catch {
    // Selection still works for this visit when local storage is unavailable.
  }
};

export const clearSavedOfficeCeLocation = (
  storage: StorageLike,
  userId: number | string,
): void => {
  try {
    storage.removeItem(getOfficeCeLocationStorageKey(userId));
  } catch {
    // Keep the in-memory reprompt path working if storage is unavailable.
  }
};

export const resolveOfficeCeLocation = (
  locations: readonly OfficeCeLocation[],
  saved: SavedOfficeLocation,
  options: { forceExplicitChoice?: boolean } = {},
): OfficeLocationSelection => {
  const available = dedupeLocations([...locations]);
  const savedLocation = saved.locationId == null
    ? undefined
    : available.find(location => location.id === saved.locationId);

  if (!options.forceExplicitChoice && savedLocation) {
    return {
      status: 'selected',
      locations: available,
      selectedLocation: savedLocation,
      selectionSource: 'saved',
      staleSelectionCleared: false,
    };
  }

  const staleSelectionCleared = saved.hadSelection && !savedLocation;
  if (available.length === 0) {
    return { status: 'empty', locations: available, staleSelectionCleared };
  }

  // An invalidated or stale selection must never fall through to the normal
  // one-office auto-select path. The member explicitly confirms again.
  if (options.forceExplicitChoice || staleSelectionCleared || available.length > 1) {
    return { status: 'selection_required', locations: available, staleSelectionCleared };
  }

  return {
    status: 'selected',
    locations: available,
    selectedLocation: available[0],
    selectionSource: 'automatic',
    staleSelectionCleared: false,
  };
};

export const initializeOfficeCeLocation = (
  storage: StorageLike,
  userId: number | string,
  locations: readonly OfficeCeLocation[],
  options: { forceExplicitChoice?: boolean } = {},
): OfficeLocationSelection => {
  const saved = readSavedOfficeCeLocation(storage, userId);
  const result = resolveOfficeCeLocation(locations, saved, options);

  if (options.forceExplicitChoice || result.staleSelectionCleared) {
    clearSavedOfficeCeLocation(storage, userId);
  }
  if (result.status === 'selected' && result.selectionSource === 'automatic') {
    saveOfficeCeLocation(storage, userId, result.selectedLocation.id);
  }
  return result;
};
